import { z } from 'zod';

import {
  Account,
  Breed,
  Donor,
  Sire,
  Site,
  StorageLocation,
  Yard,
} from '@/rtk-query.gql';

/**
 * Shallow reference to a GraphQL entity ({ id, name }). Embedding the full
 * generated GQL entity types in form schemas makes react-hook-form's
 * FieldPath<T> template-literal expansion recurse through the entire
 * (mutually recursive) entity graph, which explodes type-checking time.
 */
type GqlRef<T> = Pick<T, Extract<keyof T, 'id' | 'name' | 'code'>>;

export const outsideEmbryoFormSchemaV2 = z
  .object({
    donorId: z.custom<GqlRef<Donor>>(
      (val) => val && typeof val === 'object',
      'Donor is required',
    ),
    sireId: z.custom<GqlRef<Sire>>(
      (val) => val && typeof val === 'object',
      'Sire is required',
    ),
    freezeOwnerId: z.custom<GqlRef<Account>>(
      (val) => val && typeof val === 'object',
      'Embryo owner is required',
    ),
    transferOwnerId: z
      .custom<
        GqlRef<Account>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid transfer owner')
      .nullish(),
    breedId: z
      .custom<
        GqlRef<Breed>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid breed')
      .nullish(),
    method: z.number().int().nullish(),
    recoveryFirmCode: z.string().nullish(),
    recoveryFirmName: z.string().nullish(),
    freshFrozen: z.number().int({ message: 'Fresh frozen is required' }),
    freezeCane: z.string().nullish(),
    creation: z.date().nullish(),
    freezeTechName: z.string().nullish(),
    freezeMethodId: z.string().uuid().nullish(),
    implantDate: z.date().nullish(),
    transferQuantity: z.number().int().nullish(),
    thawByName: z.string().nullish(),
    implantTierOf: z.number().int().nullish(),
    transferSiteId: z
      .custom<
        GqlRef<Site>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid transfer site')
      .nullish(),
    transferClinicRanchHaulIn: z.number().int().nullish(),
    transferTechName: z.string().nullish(),
    implantType: z.coerce.number().nullish(),
    recipientOwnerId: z
      .custom<
        GqlRef<Account>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid recipient owner')
      .nullish(),
    deyardId: z
      .custom<
        GqlRef<Yard>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid yard')
      .nullish(),
    freezeQuantity: z.number().int().nullish(),
    freezeStorageLocationId: z
      .custom<
        GqlRef<StorageLocation>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid storage location')
      .nullish(),
    freezeTime: z.string().nullish(),
    sex: z.number().int().nullish(),
    freezer: z.string().nullish(),
    receivedOn: z.date().nullish(),
    freezeTt: z.boolean().nullish(),
    stage: z.number().int().nullish(),
    grade: z.number().int().nullish(),
  })
  .refine(
    (data) => {
      const transferQty = data.transferQuantity ?? 0;
      const freezeQty = data.freezeQuantity ?? 0;
      return transferQty > 0 || freezeQty > 0;
    },
    {
      message:
        'Either transfer quantity or freeze quantity must be greater than 0',
      path: ['transferQuantity'],
    },
  )
  .refine(
    (data) => {
      const transferQty = data.transferQuantity ?? 0;
      if (transferQty === 0) {
        return true;
      }
      return data.implantDate !== undefined && data.implantDate !== null;
    },
    {
      message:
        'Implant date is required when transfer quantity is greater than 0',
      path: ['implantDate'],
    },
  )
  .refine(
    (data) => {
      const transferQty = data.transferQuantity ?? 0;
      if (transferQty === 0) {
        return true;
      }
      return data.transferSiteId !== undefined && data.transferSiteId !== null;
    },
    {
      message:
        'Transfer site is required when transfer quantity is greater than 0',
      path: ['transferSiteId'],
    },
  )
  .refine(
    (data) => {
      const transferQty = data.transferQuantity ?? 0;
      if (transferQty === 0) {
        return true;
      }
      return (
        data.transferClinicRanchHaulIn !== undefined &&
        data.transferClinicRanchHaulIn !== null
      );
    },
    {
      message:
        'Clinic/ranch/haul in is required when transfer quantity is greater than 0',
      path: ['transferClinicRanchHaulIn'],
    },
  )
  .refine(
    (data) => {
      const freezeQty = data.freezeQuantity ?? 0;
      if (freezeQty === 0) {
        return true;
      }
      return (
        data.freezeCane !== undefined &&
        data.freezeCane !== null &&
        data.freezeCane !== ''
      );
    },
    {
      message: 'Cane is required when freeze quantity is greater than 0',
      path: ['freezeCane'],
    },
  )
  .refine(
    (data) => {
      const freezeQty = data.freezeQuantity ?? 0;
      if (freezeQty === 0) {
        return true;
      }
      return (
        data.freezeStorageLocationId !== undefined &&
        data.freezeStorageLocationId !== null
      );
    },
    {
      message:
        'Storage location is required when freeze quantity is greater than 0',
      path: ['freezeStorageLocationId'],
    },
  );

export const outsideEmbryoFormSchema = z
  .object({
    donorId: z.custom<GqlRef<Donor>>(
      (val) => val && typeof val === 'object',
      'Donor is required',
    ),
    sireId: z.custom<GqlRef<Sire>>(
      (val) => val && typeof val === 'object',
      'Sire is required',
    ),
    freezeOwnerId: z.custom<GqlRef<Account>>(
      (val) => val && typeof val === 'object',
      'Embryo owner is required',
    ),
    transferOwnerId: z
      .custom<
        GqlRef<Account>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid transfer owner')
      .nullish(),
    breedId: z
      .custom<
        GqlRef<Breed>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid breed')
      .nullish(),
    method: z.number().int().nullish(),
    recoveryFirmCode: z.string().nullish(),
    recoveryFirmName: z.string().nullish(),
    freshFrozen: z.number().int({ message: 'Fresh frozen is required' }),
    freezeCane: z.string().nullish(),
    creation: z.date().nullish(),
    freezeTechName: z.string().nullish(),
    freezeMethodId: z.string().uuid().nullish(),
    implantDate: z.date().nullish(),
    transferTechName: z.string().nullish(),
    implantType: z.coerce.number().nullish(),
    transferQuantity: z.number().int().nullish(),
    thawByName: z.string().nullish(),
    implantTierOf: z.number().int().nullish(),
    transferSiteId: z
      .custom<
        GqlRef<Site>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid transfer site')
      .nullish(),
    transferClinicRanchHaulIn: z.number().int().nullish(),
    recipientOwnerId: z
      .custom<
        GqlRef<Account>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid recipient owner')
      .nullish(),
    deyardId: z
      .custom<
        GqlRef<Yard>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid yard')
      .nullish(),
    freezeQuantity: z.number().nullish(),
    freezeStorageLocationId: z
      .custom<
        GqlRef<StorageLocation>
      >((val) => !val || (val && typeof val === 'object'), 'Invalid storage location')
      .nullish(),
    receivedOn: z.date().nullish(),
    freezeTt: z.boolean().nullish(),
    stage: z.number().int().nullish(),
    grade: z.number().int().nullish(),
  })
  .refine(
    (data) => {
      const transferQty = data.transferQuantity ?? 0;
      const freezeQty = data.freezeQuantity ?? 0;
      return transferQty > 0 || freezeQty > 0;
    },
    {
      message:
        'Either transfer quantity or freeze quantity must be greater than 0',
      path: ['transferQuantity'],
    },
  )
  .refine(
    (data) => {
      const transferQty = data.transferQuantity ?? 0;
      if (transferQty === 0) {
        return true;
      }
      return data.implantDate !== undefined && data.implantDate !== null;
    },
    {
      message:
        'Implant date is required when transfer quantity is greater than 0',
      path: ['implantDate'],
    },
  )
  .refine(
    (data) => {
      const transferQty = data.transferQuantity ?? 0;
      if (transferQty === 0) {
        return true;
      }
      return data.transferSiteId !== undefined && data.transferSiteId !== null;
    },
    {
      message:
        'Transfer site is required when transfer quantity is greater than 0',
      path: ['transferSiteId'],
    },
  )
  .refine(
    (data) => {
      const transferQty = data.transferQuantity ?? 0;
      if (transferQty === 0) {
        return true;
      }
      return (
        data.transferClinicRanchHaulIn !== undefined &&
        data.transferClinicRanchHaulIn !== null
      );
    },
    {
      message:
        'Clinic/ranch/haul in is required when transfer quantity is greater than 0',
      path: ['transferClinicRanchHaulIn'],
    },
  );

export type OutsideEmbryoFormValues = z.infer<typeof outsideEmbryoFormSchema>;
export type OutsideEmbryoFormValuesV2 = z.infer<
  typeof outsideEmbryoFormSchemaV2
>;
